// Sprite spawn data placed on the live Fortnite map (chapter map served by
// fortnite.gg's CDN). The map uses Leaflet's L.CRS.Simple with a single
// 128x128 tile grid at zoom 7 (one tile = 256px). We translate the official
// Fortnite POIs into Leaflet [y, x] coords in that pixel space.

import { SPRITES } from '../mock';

// fortnite.gg tile config (current chapter)
export const FORTNITE_MAP_VERSION = '41.01';
export const TILE_URL = `https://fortnite.gg/maps/${FORTNITE_MAP_VERSION}/{z}/{x}/{y}.webp`;
export const TILE_SIZE = 256;
export const MAX_NATIVE_ZOOM = 7;
// At native zoom, world is 128x128 tiles = 32768x32768 px.
// The actual playable map roughly covers x: 52..76, y: 52..76 in tile coords
// (so ~25 tiles wide). We expose this center for initial framing.
export const MAP_CENTER_TILE = { x: 63.5, y: 63.5 };

// Convert tile [tx, ty] at native zoom → Leaflet [y, x] pixel coords used by
// L.CRS.Simple: y goes down, x goes right.
function tileToLatLng(tx, ty, subX = 0.5, subY = 0.5) {
  const px = (tx + subX) * TILE_SIZE;
  const py = (ty + subY) * TILE_SIZE;
  return [py, px]; // Leaflet [y, x] in Simple CRS
}

// Current Chapter POIs (Fortnite Chapter 6 Season "Galactic Battle" / 41.01).
// Coordinates are approximations based on the live fortnite.gg map — they're
// close enough to land on the right region. Names match Epic's official POIs.
export const POIS = [
  { id: 'demons-domain',     name: "Demon's Domain",     pos: tileToLatLng(60, 56), type: 'named',    tags: ['fire', 'demon', 'galaxy'] },
  { id: 'crackshots-cabin',  name: "Crackshot's Cabin",  pos: tileToLatLng(55, 56), type: 'named',    tags: ['ice', 'snow'] },
  { id: 'masked-meadows',    name: 'Masked Meadows',     pos: tileToLatLng(58, 60), type: 'named',    tags: ['earth', 'base'] },
  { id: 'magic-mosses',      name: 'Magic Mosses',       pos: tileToLatLng(62, 58), type: 'named',    tags: ['water', 'dream'] },
  { id: 'first-order-base',  name: 'First Order Base',   pos: tileToLatLng(64, 56), type: 'named',    tags: ['gold', 'rare', 'epic'] },
  { id: 'kappa-kappa-factory',name: 'Kappa Kappa Factory',pos: tileToLatLng(66, 58), type: 'named',    tags: ['ghost', 'galaxy'] },
  { id: 'foxy-floods',       name: 'Foxy Floods',        pos: tileToLatLng(67, 61), type: 'named',    tags: ['water', 'duck'] },
  { id: 'outpost-enclave',   name: 'Outpost Enclave',    pos: tileToLatLng(62, 64), type: 'named',    tags: ['legendary'] },
  { id: 'shogun-x',          name: "Shogun's X",         pos: tileToLatLng(60, 65), type: 'named',    tags: ['demon', 'fire'] },
  { id: 'pumped-power',      name: 'Pumped Power',       pos: tileToLatLng(64, 65), type: 'named',    tags: ['gold', 'lightning'] },
  { id: 'sleepy-sound',      name: 'Sleepy Sound',       pos: tileToLatLng(66, 67), type: 'named',    tags: ['water', 'dream'] },
  { id: 'utopia-city',       name: 'Utopia City',        pos: tileToLatLng(58, 66), type: 'named',    tags: ['mythic', 'special'] },

  // Landmarks
  { id: 'mt-olympus',        name: 'Mount Olympus',      pos: tileToLatLng(63, 53), type: 'landmark', tags: ['mythic'] },
  { id: 'the-citadel',       name: 'The Citadel',        pos: tileToLatLng(61, 63), type: 'landmark', tags: ['legendary'] },
  { id: 'mighty-monument',   name: 'Mighty Monument',    pos: tileToLatLng(64, 60), type: 'landmark', tags: ['epic'] },
  { id: 'galaxy-rift',       name: 'Galaxy Rift',        pos: tileToLatLng(57, 63), type: 'landmark', tags: ['galaxy'] },

  // Loot zones
  { id: 'fishstick-fuel',    name: 'Fishstick Fuel',     pos: tileToLatLng(68, 64), type: 'loot',     tags: ['gummy'] },
  { id: 'reckless-railways', name: 'Reckless Railways',  pos: tileToLatLng(65, 62), type: 'loot',     tags: ['lightning'] },
  { id: 'supply-drop-zone',  name: 'Supply Drop Zone',   pos: tileToLatLng(59, 58), type: 'loot',     tags: ['base', 'gold'] },
];

export const SPAWN_TYPES = [
  { id: 'chest',     label: 'Chest',      color: '#ffd900' },
  { id: 'floor',     label: 'Floor Loot', color: '#9d3dff' },
  { id: 'npc',       label: 'NPC',        color: '#00b8ff' },
  { id: 'boss',      label: 'Boss',       color: '#ef4444' },
  { id: 'vault',     label: 'Vault',      color: '#10b981' },
];

// Deterministic string→int hash
function hash(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function rand(seed) {
  let t = seed + 0x6D2B79F5;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function pickPoi(sprite, h) {
  const fam = (sprite.family || '').toLowerCase();
  const candidates = POIS.filter(
    (p) => p.tags.includes(sprite.variant) || p.tags.includes(fam)
  );
  const pool = candidates.length ? candidates : POIS;
  return pool[h % pool.length];
}

// Each sprite snaps to a thematic POI with a small jitter so pins don't stack.
export const SPRITE_SPAWNS = SPRITES.map((sprite) => {
  const h = hash(`${sprite.id}-${sprite.name}`);
  const poi = pickPoi(sprite, h);
  const a = rand(h);
  const b = rand(h + 1);
  const angle = a * Math.PI * 2;
  const radius = TILE_SIZE * 0.4 + b * TILE_SIZE * 0.6; // 100–250 px jitter
  const pos = [
    poi.pos[0] + Math.sin(angle) * radius,
    poi.pos[1] + Math.cos(angle) * radius,
  ];
  const spawn = SPAWN_TYPES[hash(`${sprite.id}-spawn`) % SPAWN_TYPES.length];
  return {
    spriteId: sprite.id,
    pos,
    poiId: poi.id,
    spawnType: spawn.id,
  };
});

// Initial map view (center on map middle)
export const INITIAL_VIEW = {
  center: tileToLatLng(MAP_CENTER_TILE.x, MAP_CENTER_TILE.y),
  zoom: 3,
};

// World bounds in CRS.Simple pixel coords (full tile grid)
export const WORLD_BOUNDS = [
  [0, 0],
  [128 * TILE_SIZE, 128 * TILE_SIZE],
];

// Back-compat: legacy single-image map size (kept so the (currently hidden)
// SpriteVaultMap.jsx still compiles while map is disabled).
export const MAP_SIZE = 1000;
